import {
    ComponentProps,
    Dispatch,
    SetStateAction,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { DrawerWrapper } from '@/components'
import {
    GetReportResponse,
    GHGEmissionsReportSettings,
    useElementWidth,
    useFiltersStore,
} from '@/hooks'
import { ReportEditHeader } from './ReportEditHeader'
import { mapFiltersToParams } from './mapFiltersToParams'
import { mapParamsToFilters } from './mapParamsToFilters'
import useNavigationGuardStore from '@/stores/useNavigationGuardStore/useNavigationGuardStore'
import {
    hasNameChanged,
    haveFiltersChanged,
    haveSettingsChanged,
} from '../../utils/detectReportChange'
import { SustainabilityFiltersContainer } from '@/features/Sustainability/components/SustainabilityFilters/SustainabilityFilters'
import {
    DEFAULT_GHG_EMISSIONS_REPORT_SETTINGS,
    DEFAULT_SUSTAINABILITY_FILTERING,
} from '@/utils/constants'
import { mapSustainabilityFiltersToParams } from './mapSustainabilityFiltersToParams'
import { GHGEmissionsDrawer } from '@/features/Sustainability/components/GHGEmissionsDrawer/GHGEmissionsDrawer'
import { GHGEmissionsReport } from '@/features/Sustainability/components/GHGEmissions/GHGEmissionsReport'
import { TableLoadingOverlay } from '@/components'
import { useTranslation } from 'react-i18next'

type ReportEditGHGEmissionsProps = {
    data: GetReportResponse
    isOpen: boolean
    setIsOpen: Dispatch<SetStateAction<boolean>>
}

type OnChangeFn = ComponentProps<
    typeof SustainabilityFiltersContainer
>['onChange']

export const ReportEditGHGEmissions: React.FC<ReportEditGHGEmissionsProps> = ({
    data: initialData,
    isOpen,
    setIsOpen,
}) => {
    const { t } = useTranslation('translations')

    const [data, setData] = useState(initialData)
    const [filters, setFilters] = useState(initialData.filters)
    const [reportSettings, setReportSettings] =
        useState<GHGEmissionsReportSettings>(
            initialData.settings &&
                typeof initialData.settings === 'object' &&
                'showTotals' in initialData.settings
                ? initialData.settings
                : DEFAULT_GHG_EMISSIONS_REPORT_SETTINGS
        )
    const [name, setName] = useState(initialData.name)
    const rootNode = useFiltersStore((state) => state.rootNode)

    const toggleButtonRef = useRef<HTMLButtonElement>(null)
    const toggleWidth = useElementWidth(toggleButtonRef)

    const parsedFilters = mapParamsToFilters(filters)

    const baselineFilters = useMemo(
        () =>
            mapSustainabilityFiltersToParams({
                ...DEFAULT_SUSTAINABILITY_FILTERING,
                node: rootNode?.nodeId !== undefined ? [rootNode.nodeId] : [],
            }),
        [rootNode?.nodeId]
    )
    const handleResetFilters = useCallback(() => {
        setFilters(baselineFilters)
    }, [baselineFilters])

    const showResetFiltersButton = useMemo(() => {
        return haveFiltersChanged(filters, baselineFilters)
    }, [filters, baselineFilters])

    const updatedReport = useMemo(
        () => ({
            ...data,
            name,
            filters,
            settings: reportSettings,
        }),
        [data, filters, name, reportSettings]
    )

    const setUnsavedChanges = useNavigationGuardStore(
        (state) => state.setUnsavedChanges
    )

    useEffect(() => {
        const filtersChanged = haveFiltersChanged(filters, initialData.filters)
        const settingsChanged = haveSettingsChanged(
            reportSettings,
            initialData.settings as GHGEmissionsReportSettings
        )
        const nameChanged = hasNameChanged(name, initialData.name)

        setUnsavedChanges(filtersChanged || nameChanged || settingsChanged)
    }, [
        name,
        filters,
        reportSettings,
        initialData.name,
        initialData.filters,
        initialData.settings,
        setUnsavedChanges,
    ])

    const handleReportSaved = () => {
        setReportSettings(updatedReport.settings)
        setFilters(updatedReport.filters)
        setName(updatedReport.name)
        setData(updatedReport)
        setUnsavedChanges(false)
    }

    const onChangeReportSettings = useCallback(
        (newSettings: Partial<GHGEmissionsReportSettings>) => {
            setReportSettings((prevSettings) => ({
                ...prevSettings,
                ...newSettings,
            }))
        },
        []
    )

    const showResetReportSettingsButton = useMemo(() => {
        return haveSettingsChanged(
            reportSettings,
            DEFAULT_GHG_EMISSIONS_REPORT_SETTINGS
        )
    }, [reportSettings])

    const onFiltersChange = useCallback<OnChangeFn>((filter, value) => {
        setFilters((prevParams) => {
            const prevFilters = mapParamsToFilters(prevParams)
            const newFilters = {
                ...prevFilters,
                [filter]: value,
            }
            const mappedFiltersToParams = mapFiltersToParams(newFilters)

            const { commodityTypeId: _commodityTypeId, ...mappedFilters } =
                mappedFiltersToParams
            return mappedFilters
        })
    }, [])

    const calculationMethodOffset = isOpen ? 16 : toggleWidth

    const [isUpdatePending, setIsUpdatePending] = useState(false)

    return (
        <div className="relative grid h-full grid-cols-[100%] grid-rows-[auto_1fr]">
            <div className="relative z-30">
                <ReportEditHeader
                    initialReportName={data.name}
                    report={updatedReport}
                    onChangeName={setName}
                    onSaveAsNewReportSuccess={handleReportSaved}
                    onUpdatePendingChange={setIsUpdatePending}
                />
                <div className="border-b border-neutral-200 p-6">
                    <SustainabilityFiltersContainer
                        reportType={data.type}
                        activeFilters={parsedFilters}
                        onChange={onFiltersChange}
                        handleResetFilters={handleResetFilters}
                        showResetFiltersButton={showResetFiltersButton}
                    />
                </div>
            </div>
            <TableLoadingOverlay
                show={isUpdatePending}
                title={t('reporting.update_report.loading_save')}
                coverContainer
            />
            <DrawerWrapper className="h-full">
                <GHGEmissionsDrawer
                    reportSettings={reportSettings}
                    setReportSettings={onChangeReportSettings}
                    isOpen={isOpen}
                    setIsOpen={setIsOpen}
                    toggleButtonRef={toggleButtonRef}
                    showResetReportSettingsButton={
                        showResetReportSettingsButton
                    }
                />
                <GHGEmissionsReport
                    reportSettings={reportSettings}
                    isOpen={isOpen}
                    filters={parsedFilters}
                    calculationMethodOffset={calculationMethodOffset}
                />
            </DrawerWrapper>
        </div>
    )
}
